import type { StockStatusEnum } from '@petsmart-ui/sparky';

import type { MappedProductVariant, ProductCategory, ProductHit } from '@libs/algolia';
import { getDefaultCategories, PharmacyType } from '@libs/algolia';
import type { ItemsListItem } from '@libs/common-next';
import { formatAutoshipFrequency, logSentryError, NavigationListTypes } from '@libs/common-next';
import type { CartUI, FulfillmentGroupsItemUI, ItemUI, OrderLine, OrderUI } from '@libs/common-node';
import {
    CreditCardsType,
    Currency,
    DeliveryType,
    getCurrency,
    getLocale,
    Locale,
    logger,
    PaymentMethodsType,
} from '@libs/core';

import { TrackingParams } from '../constants/trackingParams';
import { TrackingEvents } from '../enums';
import {
    getDiscountCodesString,
    getFulfillmentMethodForItem,
    getFulfillmentMethodsString,
    getPaymentMethodsString,
} from '../helpers/analytics';
import { buildFulfillmentMethodLabel } from '../helpers/buildFulfillmentMethodLabel';
import { resolveGa4ItemListAttributionForCartItem } from '../helpers/resolveGa4ItemListAttributionForCartItem';
import { addScheduledDeliveryDateLabel, formatScheduledDeliveryDate } from '../helpers/scheduledDeliveryDateUtils';
import { getSkuSearchTerm, resolveItemSearchTerm } from '../helpers/skuSearchTermStorage';
import { getSearchStats } from '../helpers/storageSearch';
import type { Category, CreateAccountEvent, EventItem, EventTracking, Product, YesNo } from '../types';
import { AUTOSHIP_DELIVERIES } from './const';
import { dataLayerPush } from './dataLayerPush';
import { getGa4ItemId } from './getGa4ItemId';
import {
    extractCategoriesFromUrl,
    getAvailabilityConstant,
    getDimensionCategories,
    getItemDiscount,
    getProductObject,
    getPromoDesc,
    getPromoOrigin,
    getRoughTotal,
    getStockLevelConstant,
    getSubmitSearchType,
    getViewSearchResultsType,
    mapCartItem,
    mapCheckoutItems,
    mapImpressions,
    mapItem,
    mapProductClick,
    roundTotal,
    toDollarsAmount,
} from './helpers';
import type {
    BaseDeliveryType,
    CardClick,
    ContentfulClickProps,
    DeliveryTypeUnion,
    EEImpressionParams,
    PageViewParams,
    ProductTileClickParams,
    SearchFlyoutClickParams,
    Tile6UpClickProps,
    TilesClickParams,
    TrackProductListFilterProps,
    UserAuthenticationParams,
    VisualNavigationClicksParams,
} from './types';
import { Tile6UpVariant } from './types';

const currencyCode = getCurrency();

type AddedProduct = {
    algoliaHit: ProductHit;
    parameters: {
        quantity: number;
        fulfillmentMethod: DeliveryType;
        type: string;
        autoshipFrequency?: number;
        storeID?: string;
    };
};

type DataTrackingObj = {
    [key: string]: unknown;
};

type Nullable<T> = T | null | undefined;

export const trackEEAddToCart = (products: AddedProduct[], captureType: string) => {
    const result = products.map(product => {
        const { algoliaHit } = product;
        const { fulfillmentMethod, storeID, type, quantity, autoshipFrequency } = product.parameters;

        // Most reliable way found to get a subcategory path
        const defaultCategories = getDefaultCategories(product.algoliaHit);

        const deliveryTypes: Record<DeliveryType, string> = {
            [DeliveryType.BOPIS]: 'bopis',
            [DeliveryType.SDD]: 'sdd',
            [DeliveryType.STM]: 'stm',
            [DeliveryType.AUTOSHIP]: 'autoship',
            [DeliveryType.STH]: 'sth',
        };

        const productObject = getProductObject(algoliaHit);

        const dimension21 =
            algoliaHit['pharmacy-type'] === PharmacyType.MED && algoliaHit.customCaseQuantity
                ? algoliaHit.customCaseQuantity
                : null;

        return {
            ...productObject,
            price: algoliaHit.price?.number,
            quantity,
            dimension9: defaultCategories[0]?.name.default,
            dimension10: defaultCategories[1]?.name.default,
            dimension11: defaultCategories[2]?.name.default,
            dimension21,
            dimension12: deliveryTypes[fulfillmentMethod],
            dimension13: autoshipFrequency,
            dimension14: storeID,
            dimension133: captureType,
            dimension6: type, // Standard PDP
            // dimension39 is not here because it's a parameter for a SFCC thing.
        };
    });

    dataLayerPush({
        event: 'EEaddToCart',
        ecommerce: {
            currencyCode,
            add: {
                actionField: {
                    list: captureType,
                },
                products: result,
            },
        },
    });
};

export const trackProductListFilter = ({
    position,
    linkUrl,
    linkText,
    promoTitle,
    promoVariant,
    filterSelection,
    filterType,
    results,
}: TrackProductListFilterProps) => {
    dataLayerPush({
        event: 'product_list_filter',
        filter_selection: filterSelection,
        filter_type: filterType,
        position,
        link_url: linkUrl,
        link_text: linkText,
        promo_title: promoTitle,
        promo_variant: promoVariant,
        results,
    });
};

export const trackDrawerFilter = ({
    filterType,
    filterSelection,
    results,
}: Pick<TrackProductListFilterProps, 'filterType' | 'filterSelection' | 'results'>) => {
    dataLayerPush({
        event: 'product_list_filter',
        filter_type: filterType,
        filter_selection: filterSelection,
        results: `${results} results`,
    });
};

export const trackContentfulClick = ({
    position,
    linkText,
    linkUrl,
    promoTitle,
    promoVariant,
    promoOrigin,
    promoDesc,
    promoType,
    skipEcommerceCleanup,
}: ContentfulClickProps) => {
    const dataLayerObject: EventTracking = {
        event: 'contentful_click',
        link_text: linkText,
        link_url: linkUrl,
        promo_title: promoTitle,
        promo_variant: promoVariant,
        ...(promoOrigin && { promo_origin: getPromoOrigin(promoOrigin) }),
        ...(promoDesc && { promo_desc: getPromoDesc(promoDesc) }),
        ...(promoType && { promo_type: promoType }),
        ...(position && { position }),
    };

    if (!skipEcommerceCleanup) {
        dataLayerPush({ ecommerce: null });
    }
    dataLayerPush(dataLayerObject);
};

export const trackCardUpClick = ({
    variant,
    title,
    cardName,
    cardPosition,
    link,
    promoType,
    promoDesc,
    pathname = '',
    linkText = '',
}: CardClick) => {
    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'contentful_click',
        position: cardPosition,
        link_text: getPromoDesc(cardName) ?? '',
        link_url: link,
        promo_title: title,
        promo_variant: variant,
        promo_origin: getPromoOrigin(pathname),
        promo_desc: promoDesc,
        promo_type: promoType,
    });
};

export const trackProductTileClick = ({ product, position, variant = null, list = null }: ProductTileClickParams) => {
    const actionField: { list: string | null; action?: string } = {
        list,
    };

    if (getLocale() === Locale['en-CA']) {
        actionField.action = 'click';
    }

    dataLayerPush({
        event: 'EEproductClick',
        ecommerce: {
            currencyCode,
            click: {
                actionField,
                products: [
                    {
                        ...mapProductClick(product),
                        position,
                    },
                ],
            },
            variant,
        },
    });
};

export const trackAddToCartMethod = (deliveryType: DeliveryType) => {
    const table: Record<string, string> = {
        [DeliveryType.BOPIS]: 'bopis',
        [DeliveryType.STM]: 'sth',
    };

    const value = table[deliveryType];

    if (value) {
        dataLayerPush({
            event: `add-to-cart ${value}`,
        });
    }
};

export const trackTabClick = (selection?: string) => {
    dataLayerPush({
        event: TrackingEvents.TAB_CLICK,
        selection,
    });
};

export type TrackActivateRewardParams = {
    pageSection: string;
    selection: string;
};

export const trackActivateReward = ({ pageSection, selection }: TrackActivateRewardParams) => {
    dataLayerPush({
        event: 'activate_reward',
        page_section: pageSection,
        selection: pageSection === 'store events' ? selection.trim().toLowerCase() : selection,
    });
};

export const velocityTrackAddToCart = (
    selectedProduct: MappedProductVariant,
    currency: Currency,
    pdpType: string,
    productRecommentation: string,
    items: EventItem[]
) => {
    const filteredCategories: ProductCategory[] = getDefaultCategories(selectedProduct) ?? [];

    const categories: Category = {
        category: filteredCategories[0]?.name?.default || '',
        sub_category1: filteredCategories[1]?.name?.default || '',
        sub_category2: filteredCategories[2]?.name?.default || '',
    };

    const roundedTotal = roundTotal(getRoughTotal(items));

    dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
            ...categories,
            currency,
            value: roundedTotal,
            pdp_type: pdpType,
            product_recommendation: productRecommentation,
            items,
        },
    });
};

export const trackEEImpression = ({ products, list }: EEImpressionParams) => {
    const impressions = mapImpressions(products, list);

    dataLayerPush({
        event: 'EEimpression',
        ecommerce: {
            currencyCode,
            impressions,
        },
    });
};

export const trackEEAddToWishlist = (product: MappedProductVariant, masterProduct: ProductHit) => {
    const masterProductData = getProductObject(masterProduct);
    dataLayerPush({
        event: 'EEaddToWishlist',
        ecommerce: {
            currencyCode,
            products: [
                {
                    ...masterProductData,
                    id: product.objectID,
                    name: product.name,
                    brand: product.brand,
                    dimension18: product.color,
                    dimension19: product.size,
                    dimension20: product.flavor,
                },
            ],
        },
    });
};

export const trackClickContinueShopping = () => {
    dataLayerPush({
        event: 'click_continue_shopping',
    });
};

export const trackEEProductDetailView = (
    product: ProductHit,
    availability: Record<string, { ats: number; status: StockStatusEnum }>
) => {
    const dimension6 = TrackingParams.STANDARD_PDP;
    const stm = availability[DeliveryType.STM];
    const autoship = availability[DeliveryType.AUTOSHIP];

    const productObj = {
        ...getProductObject(product),
        ...getDimensionCategories(product),
        price: product.price?.number,
        dimension6,
        dimension23: getAvailabilityConstant(stm.ats),
        dimension25: getAvailabilityConstant(autoship.ats),
        dimension37: getStockLevelConstant(stm.status),
        dimension39: product.id === product.masterProductID ? TrackingParams.YES : TrackingParams.NO,
        dimension47: getStockLevelConstant(autoship.status),
    };

    dataLayerPush({
        event: 'EEproductDetailView',
        ecommerce: {
            currencyCode,
            detail: {
                actionField: {
                    list: dimension6,
                },
                products: [productObj],
            },
        },
    });
};

export const trackGlobalPromoBanner = (text: string) => {
    dataLayerPush({
        event: 'GlobalPromotionBannerClicks',
        global_promotion_banner_name: text,
    });
};

export const trackPLPSortingOptions = (optionName: string) => {
    dataLayerPush({
        event: 'PLPSortOptions',
        sortBy: optionName,
    });
};

export const trackTile6UpClick = ({ variant, name, position, navigationClick }: Tile6UpClickProps) => {
    const event = {
        [Tile6UpVariant.SHOPPABLE]: 'ShoppableBannerClick',
        [Tile6UpVariant.PROMO]: 'SixPromoTileClick',
    }[variant];

    if (variant === Tile6UpVariant.SHOPPABLE) {
        dataLayerPush({
            event,
            shoppable_banner_name: name,
            shoppable_banner_position: position,
            navigation_click: navigationClick,
        });
    }

    if (variant === Tile6UpVariant.PROMO) {
        dataLayerPush({
            event,
            promo_banner_name: name,
            promo_banner_position: position,
            navigation_click: navigationClick,
        });
    }
};

export const trackUserAuthentication = ({
    totalTreatsPoints,
    loyaltyId,
    storeId,
    loginState,
}: UserAuthenticationParams) => {
    dataLayerPush({
        event: 'UserAuthentication',
        totalTreatsPoints,
        loyaltyId,
        storeId,
        loginState,
    });
};

export const trackPromoBannerCTAClick = (bannerTitle: string, variant: string, clickText: string) => {
    dataLayerPush({
        event: 'PromoBannerClick',
        bannerTitle,
        variant,
        clickText,
    });
};

export const trackMainNavLinkClick = (linkText: string) => {
    dataLayerPush({
        event: 'MainNavClicks',
        clickTerm: linkText,
    });
};

export const trackLegacySearchEvent = (term: string, searchAssistName?: string) => {
    dataLayerPush({
        event: TrackingEvents.LEGACY_SEARCH,
        searchType: getSubmitSearchType(searchAssistName),
        searchAssist: searchAssistName,
        searchTerm: term,
    });
};

export const trackSubmitSearchEvent = (searchTerm: string, searchAssistName?: string, searchAssistTerm?: string) => {
    dataLayerPush({
        event: TrackingEvents.SUBMIT_SEARCH,
        search_type: getSubmitSearchType(searchAssistName),
        search_assist: searchAssistName?.toString().toLowerCase(),
        search_assist_term: searchAssistTerm,
        search_term: searchTerm,
    });
};

export const trackViewSearchResultsEvent = (
    term: string,
    numberOfResults: number,
    searchType?: string,
    searchAssistName?: string,
    searchAssistTerm?: string
) => {
    dataLayerPush({
        event: TrackingEvents.VIEW_SEARCH_RESULTS,
        search_type: getViewSearchResultsType(numberOfResults, searchType),
        search_term: term,
        results: numberOfResults,
        search_assist: searchAssistName?.toLowerCase(),
        search_assist_term: searchAssistTerm,
    });
};

export const trackViewSearchResultsPdp = (term: string, results: number, searchType?: string) => {
    dataLayerPush({
        event: TrackingEvents.VIEW_SEARCH_RESULTS,
        search_type: getViewSearchResultsType(results, searchType),
        search_term: term,
        results,
    });
};

export const trackTilesClicks = ({ variant, header, title, position, row }: TilesClickParams) => {
    dataLayerPush({
        event: 'Tile Clicks',
        variant,
        title: header,
        tile_description: title,
        tile_position: position, // -1 if action link is clicked
        tile_row: row, // -1 if action link is clicked
    });
};

export const trackProductView = (productName: string) => {
    dataLayerPush({
        event: 'product view',
        pageName: productName,
    });
};

export const trackPLPFilters = (filterCategory: string, filter: string) => {
    dataLayerPush({
        event: 'PLPFilters',
        filterCategory,
        filter,
    });
};

export const trackAutoshipQuickFilter = (nbHits: number) => {
    dataLayerPush({
        event: 'product_list_filter',
        filter_type: 'quick filter',
        filter_selection: 'autoship',
        results: `${nbHits} results`,
    });
};

export const trackAddToCart = () => {
    dataLayerPush({
        event: 'addToCart',
    });
};

export const trackError = (pageType: string, errorMessage: string, detail?: string) => {
    dataLayerPush({
        event: TrackingEvents.ERROR,
        site_section: pageType,
        error_message: errorMessage,
        error_detail: detail ?? '',
    });

    const errorMessagesSplit = errorMessage.split('|');

    if (errorMessagesSplit.length) {
        for (const message of errorMessagesSplit) {
            dataLayerPush({
                event: 'treatErrorMessageSplit',
                error_message: message.trim(),
                error_detail: detail ?? '',
            });
        }
    }
};

export const trackErrorCustom = (siteSection: string, errorMessage: string) => {
    dataLayerPush({
        event: 'error_custom',
        site_section: siteSection,
        error_message: errorMessage,
    });
};

export const trackGotchaDayError = (errorMessage: string) => {
    dataLayerPush({
        event: 'error_custom',
        site_section: 'my account | pets',
        error_message: errorMessage.toLowerCase(),
    });
};

export const trackSearchFlyoutClick = ({ term, header, url }: SearchFlyoutClickParams) => {
    dataLayerPush({
        event: 'search-flyout-click',
        'search-flyout-destinationURL': url,
        'search-flyout-searchTerm': term,
        'search-flyout-sectionHeader': header,
    });
};

export const trackPDPFindingMethod = (findingMethod: string) => {
    dataLayerPush({
        event: 'pdpFindMethod',
        findingMethod,
    });
};

export const trackVisualNavigationClicks = ({ categoryId, navPos, text }: VisualNavigationClicksParams) => {
    dataLayerPush({
        event: 'VisualNavigationClicks',
        categoryId,
        navigation_position: navPos,
        itemText: text,
    });
};

export type TrackProductTileClickItemProps = {
    [key: string]: unknown;
};

export type TrackProductTileClickProps = {
    product: Product;
    /**
     * 1-based position of the tile within its list. When omitted, `index` is left off the payload so
     * existing callers keep their current event shape.
     */
    listPosition?: number;
    /**
     * Controls the `pdp_type` field. Omitting it keeps the default `'standard pdp'` value.
     * Pass `null` to suppress the field entirely for placements where the spec excludes it.
     */
    pdpType?: string | null;
    ecommerce: {
        [key: string]: unknown;
        items: TrackProductTileClickItemProps[];
    };
};

export const trackProductTileItemClick = ({
    product,
    listPosition,
    pdpType,
    ecommerce,
}: TrackProductTileClickProps) => {
    const dataObj: DataTrackingObj = {
        quantity: 1,
        size: product.itemSize ?? '',
        flavor: product.flavor?.value ?? '',
        item_variant: product.objectID,
        color: product.color?.value ?? '',
    };

    const { index: _listPosition, ...itemPayload } = {
        ...ecommerce.items[0],
        ...dataObj,
    };

    const dataLayerObject: DataTrackingObj = {
        event: 'select_item',
        ecommerce: {
            currency: ecommerce.currency,
            ...(pdpType !== null && { pdp_type: pdpType ?? NavigationListTypes.STANDARD_PDP }),
            category: ecommerce.category,
            sub_category1: ecommerce.sub_category1,
            sub_category2: ecommerce.sub_category2,
            items: [
                {
                    item_name: itemPayload.item_name,
                    item_id: itemPayload.item_id,
                    quantity: itemPayload.quantity,
                    price: itemPayload.price,
                    item_category: itemPayload.item_category,
                    item_category2: itemPayload.item_category2,
                    item_category3: itemPayload.item_category3,
                    item_category4: itemPayload.item_category4,
                    item_category5: itemPayload.item_category5,
                    item_brand: itemPayload.item_brand,
                    item_variant: itemPayload.item_variant,
                    ...(listPosition !== undefined && { index: listPosition }),
                    item_list_name: itemPayload.item_list_name,
                    item_list_id: itemPayload.item_list_id,
                    color: itemPayload.color,
                    size: itemPayload.size,
                    flavor: itemPayload.flavor,
                    item_search_term: itemPayload.item_search_term,
                    ...(itemPayload.existing_subscription != null && {
                        existing_subscription: itemPayload.existing_subscription,
                    }),
                },
            ],
        },
    };

    dataLayerPush({ ecommerce: null });
    dataLayerPush(dataLayerObject);
};

export const trackApplyFacetedFilters = (filterType: string, filterSelection: string, pageId: string) => {
    dataLayerPush({
        action: 'applyFilter_SearchResults',
        event: 'applyFacetedFilter',
        label: `${filterType}_${filterSelection}`,
        linkPageName: `ps:blip:${pageId}`,
    });
};

export const trackSignUpSubmit = () => {
    dataLayerPush({
        event: 'user-signup-submit',
    });
};

export const trackSignUpSuccess = () => {
    dataLayerPush({
        event: 'user-signup-success',
    });
};

export const trackTreatsSignUpSuccess = () => {
    dataLayerPush({
        event: 'TreatsSignupSuccess',
    });
};

export const trackPersonalizationSignUpSuccess = (personalization: string[]) => {
    dataLayerPush({
        event: 'personalization',
        personalization: personalization.join(','),
    });
};

export const trackSignUpFailure = (accountCreationFailureReason: string) => {
    dataLayerPush({
        event: 'user-signup-failed',
        accountCreationFailureReason,
    });
};

export const trackTreatsSignUpFailure = (errorMessages: string[]) => {
    const errorCount = errorMessages.length;
    dataLayerPush({
        event: 'TreatsSignupFail',
        errorCount,
        errorMessage: errorMessages[0],
    });

    trackError('create account', errorMessages.join('|'));
};

/**
 * Returns `undefined` for every loyalty value analytics must not report: an absent field,
 * the `'pending'` placeholder the post-login Action sets while no loyalty id exists yet,
 * the `0` the id-token mapping falls back to for a missing claim, and anything non-numeric.
 */
export const toReportableLoyaltyNumber = (value: number | string | null | undefined): number | undefined => {
    const parsed = typeof value === 'string' ? Number(value) : value;

    if (typeof parsed !== 'number' || !Number.isFinite(parsed) || parsed === 0) {
        return undefined;
    }

    return parsed;
};

export type TrackCreateAccountParams = {
    loyaltyId?: number | string | null;
    treatsPoints?: number | null;
    currentTierRank?: number | null;
};

/**
 * GA4 `create_account` for an Auth0 registration. Loyalty data is whatever is already
 * known when the shopper lands back on PetSmart — a brand-new account usually has none,
 * so every unavailable field is omitted instead of being sent as a placeholder.
 */
export const trackCreateAccount = ({ loyaltyId, treatsPoints, currentTierRank }: TrackCreateAccountParams = {}) => {
    const reportableLoyaltyId = toReportableLoyaltyNumber(loyaltyId);
    const reportableTreatsPoints = toReportableLoyaltyNumber(treatsPoints);
    const reportableTierRank = toReportableLoyaltyNumber(currentTierRank);

    const event: CreateAccountEvent = {
        event: 'create_account',
        ...(reportableLoyaltyId !== undefined && { loyalty_id: reportableLoyaltyId }),
        ...(reportableTreatsPoints !== undefined && { treats_points: reportableTreatsPoints }),
        // `tier ${rank}` keeps the shape the login event already sends.
        ...(reportableTierRank !== undefined && { treats_tier: `tier ${reportableTierRank}` }),
    };

    dataLayerPush(event);
};

export const trackChangePassword = (section: string) => {
    dataLayerPush({
        event: 'change_password',
        site_section: section,
    });
};

export const trackResetPassword = () => {
    dataLayerPush({
        event: 'reset_password',
    });
};

export const trackPreferencesSave = (preferences: {
    petPreferences: {
        isDogPreferred?: boolean;
        isCatPreferred?: boolean;
        isBirdPreferred?: boolean;
        isFishPreferred?: boolean;
        isReptilePreferred?: boolean;
        isSmallPetPreferred?: boolean;
    };
    isEmailOptedIn?: boolean;
    isTextOptedIn?: boolean;
}) => {
    const petPreferenceMap: Record<string, string> = {
        isDogPreferred: 'dog',
        isCatPreferred: 'cat',
        isBirdPreferred: 'bird',
        isFishPreferred: 'fish',
        isReptilePreferred: 'reptile',
        isSmallPetPreferred: 'small pet',
    };

    const selectedPetPreferences = Object.entries(preferences.petPreferences)
        .filter(([_, isSelected]) => isSelected)
        .map(([key]) => petPreferenceMap[key])
        .filter(Boolean);

    const communicationPreferences: string[] = [];
    if (preferences.isTextOptedIn) {
        communicationPreferences.push('text');
    }
    if (preferences.isEmailOptedIn) {
        communicationPreferences.push('email');
    }

    dataLayerPush({
        event: 'save_about_you',
        pet_preference: selectedPetPreferences.length > 0 ? selectedPetPreferences.join('|') : '',
        communication_preference: communicationPreferences.length > 0 ? communicationPreferences.join('|') : '',
    });
};

export const trackOrderPageView = (params: PageViewParams) => {
    dataLayerPush({
        event: 'page_view',
        ...params,
    });
};

export const trackAccountAddToCart = (orderCurrency: string, orderLines: OrderLine[], list: string) => {
    const products = orderLines.map(line => ({
        name: line.itemDescription?.replace(/['"`]/g, '\\$&'), // Product name with escaped quotes
        id: line.itemId, // // Unique product identifier
        price: line.unitPriceAmount, // Price of the product
        brand: line.product.brand, // Merchandise brand of the product.
        quantity: line.orderedQuantity, // Quantity of the item added to the cart
        dimension12: line.fulfillmentMethod?.toLowerCase() ?? null, // Fulfillment method, options include STH, BOPIS, Autoship, SDD
        dimension6: list, // Type of PDP (Product Detail Page) add-to-cart
        dimension39: 'Yes', // Placeholder for additional attribute; set to "Yes"
        dimension19: line.itemSize ?? null, // Size of the item; null if not applicable
        dimension21: null, // Placeholder for an additional attribute, null by default
        dimension13: null, // Autoship frequency; null if not applicable
        dimension9: line.product.category, // Category of the item, e.g., "dog", "cat", "reptile", "fish"
        dimension10: line.product.subCategory1, // Subcategory of the item (e.g., Treats, Food)
        dimension14: line.shippingInfo?.shipToFacility ?? null, // Store ID for BOPIS (buy online, pick up in store), null if not applicable
        dimension11: line.product.subCategory2, // More specific subcategory
        dimension5: line.product.pharmacyType ?? 'Others', // Product type, options include Others, Vet Diet, Pharmacy
        dimension20: line.product.flavor ?? null, // Flavor of the item; null if not applicable
        dimension18: line.product.color ?? null, // Color of the item; null if not applicable
        dimension41: line.product.vicePresidentName ?? null, // Value Proposition (VP) category
        dimension42: line.product.hierarchyDivision ?? null, // Division category
        dimension43: line.product.hierarchyDepartment ?? null, // Department category
        dimension44: line.product.hierarchyClass ?? null, // Class within the department
        dimension45: line.product.hierarchyCategory ?? null, // Specific category within the class
    }));

    // Clear previous ecommerce data in dataLayer
    dataLayerPush({
        ecommerce: null,
    });

    // Push new ecommerce data for an "add to cart" event
    dataLayerPush({
        event: 'EEaddToCart',
        ecommerce: {
            currencyCode: orderCurrency,
            add: {
                actionField: {
                    list,
                    action: 'add',
                },
                products,
            },
        },
    });
};

type AdditionalData = {
    category: string;
    sub_category1: string;
    sub_category2: string;
    promisedShipDate: string | null;
    productRecommendation?: YesNo;
};
/**
 * GA4 add_to_cart event for Scheduled order details reorders.
 *
 * This is intentionally additive and does not replace legacy `trackAccountAddToCart`.
 */
export const trackScheduledOrderDetailsAddToCart = (payload: {
    cart: CartUI;
    enrichedItems: ItemsListItem[];
    addToCartType: 'reorder item' | 'reorder all';
    additionalData: AdditionalData;
}) => {
    const { cart, enrichedItems, addToCartType, additionalData } = payload;
    const { category, sub_category1, sub_category2, productRecommendation } = additionalData;

    dataLayerPush({ ecommerce: null });

    const itemMap = new Map(enrichedItems.map(item => [item.id, item]));

    const items = cart.fulfillmentGroups.flatMap(group =>
        group.items.map(item => ({
            ...buildGa4ItemTrackingFields({
                item: itemMap.get(item.id) as ItemsListItem,
                includeStockAvailability: true,
                group,
            }),
            item_list_name: 'order details',
            item_list_id: 'order details',
            // scheduled_autoship_date: formatScheduledDeliveryDate(promisedShipDate),
        }))
    );

    dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
            currency: cart.currency,
            value: cart.summary?.totalAmount ?? 0,
            pdp_type: 'order details',
            add_to_cart_type: addToCartType,
            category,
            sub_category1,
            sub_category2,
            product_recommendation: productRecommendation,
            items,
        },
    });
};

export const trackViewEmptyCart = () => {
    dataLayerPush({
        event: 'view_empty_cart',
    });
};

export const trackAddCartDonation = (currency: Currency, value: number, itemsCount = 0) => {
    const order = itemsCount + 1;

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
            currency,
            value: (value / 100).toFixed(2),
            pdp_type: 'cart',
            category: 'donation',
            add_to_cart_type: 'add',
            items: [
                {
                    item_name: 'donation',
                    item_id: '1234',
                    quantity: 1,
                    price: (value / 100).toFixed(2),
                    item_category: 'donation',
                    item_cart_order: order,
                    add_to_cart_type: 'add',
                },
            ],
        },
    });
};

export const trackViewCart = (items: ItemsListItem[], currencyCode = Currency.USD) => {
    const products = items.map(mapCartItem);

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEviewCart',
        ecommerce: {
            currencyCode,
            checkout: {
                products,
            },
        },
    });
};

export const velocityTrackViewCart = (items: TrackCartItem[], currency = Currency.USD) => {
    const roughTotal = items.reduce<number>((acc, item) => acc + (item.salePrice ?? 0), 0);
    const updatedItems = items.map(({ salePrice, ...item }) => item);
    const roundedTotal = roundTotal(roughTotal / 100);

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'view_cart',
        ecommerce: {
            currency,
            value: roundedTotal,
            items: updatedItems,
        },
    });
};

export const trackCartUpdateQuantity = (item: ItemUI, currencyCode = Currency.USD) => {
    const products = [mapItem(item)];

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEaddToCart',
        ecommerce: {
            currencyCode,
            add: {
                actionField: {
                    list: 'Cart',
                    action: 'add',
                },
                products,
            },
        },
    });
};

export type TrackCartItem = {
    item_name: string;
    quantity: number;
    price: number;
    item_category: string;
    item_category2: string;
    item_category3: string;
    item_category4: string;
    item_category5: string;
    item_brand: string;
    item_variant: string;
    item_list_name: string;
    item_list_id: string;
    /** Set when discovery source uses the 3-part fref-style format (e.g. BIA carousel). */
    index?: number;
    fulfillment_method: string;
    autoship_frequency: string | null;
    store_id: string | null;
    color: string;
    size: string;
    flavor: string;
    item_search_term: string | null;
    add_to_cart_type?: string;
    autoship_availability: string;
    autoship_stock_availability?: string;
    sth_availability?: string;
    bopis_availability?: string;
    sdd_availability?: string;
    sth_stock_availability?: string;
    bopis_stock_availability?: string;
    sdd_stock_availability?: string;
    salePrice: number | undefined;
    scheduled_autoship_date: string | null;
};

export const velocityTrackUpdateQuantity = (
    currency: Currency | undefined,
    items: TrackCartItem[],
    cartType: string
) => {
    const roughTotal = items.reduce<number>((acc, item) => acc + (item.salePrice ?? 0), 0);
    const updatedItems = items.map(({ salePrice, ...item }) => item);
    const roundedTotal = roundTotal(roughTotal / 100);

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: cartType?.includes('decrease') ? 'remove_from_cart' : 'add_to_cart',
        ecommerce: {
            currency,
            value: roundedTotal,
            pdp_type: 'cart',
            category: '', // Skipped at this moment, in the future it would be integrated
            sub_category1: '', // Skipped at this moment, in the future it would be integrated
            sub_category2: '', // Skipped at this moment, in the future it would be integrated
            add_to_cart_type: cartType,
            items: updatedItems,
        },
    });
};

export const trackCartFulfillmentMethodChange = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    previousFulfillmentMethod: DeliveryType | string;
    selectedFulfillmentMethod: DeliveryType | string;
    previousScheduledDeliveryDate?: string | null;
    newScheduledDeliveryDate?: string | null;
}) => {
    const {
        cart,
        item,
        previousFulfillmentMethod,
        selectedFulfillmentMethod,
        previousScheduledDeliveryDate = item.scheduledDeliveryDate,
        newScheduledDeliveryDate = item.scheduledDeliveryDate,
    } = payload;
    dataLayerPush({
        event: 'change_fulfillment_method',
        value: item.salePrice.toFixed(2),
        currency: cart?.currency,
        old_selection: addScheduledDeliveryDateLabel(
            previousFulfillmentMethod?.toLowerCase(),
            previousScheduledDeliveryDate
        ),
        new_selection: addScheduledDeliveryDateLabel(
            selectedFulfillmentMethod?.toLowerCase(),
            newScheduledDeliveryDate
        ),
        product_id: item.sku,
        product_price: Number((item.salePrice / item.quantity).toFixed(2)),
        product_quantity: item.quantity,
        scheduled_autoship_date: formatScheduledDeliveryDate(newScheduledDeliveryDate),
    });
};

export const trackAutoshipSetup = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    fulfillmentMethod?: DeliveryType | string;
}) => {
    const { cart, item, fulfillmentMethod } = payload;

    type AutoshipSetupFulfillmentMethodOrder =
        | 'bopis autoship'
        | 'bopis one-time'
        | 'sdd autoship'
        | 'sdd one-time'
        | 'sth autoship'
        | 'sth one-time';

    const normalizeFulfillmentMethodOrder = (
        method?: DeliveryType | string
    ): AutoshipSetupFulfillmentMethodOrder | undefined => {
        if (!method) return undefined;

        const normalizedMethod = method.toLowerCase().trim();
        const hasAutoship = Boolean(item.autoshipFrequency);
        const autoshipStatus = hasAutoship ? 'autoship' : 'one-time';

        const table: Record<string, 'bopis' | 'sdd' | 'sth'> = {
            [DeliveryType.BOPIS]: 'bopis',
            [DeliveryType.SDD]: 'sdd',
            [DeliveryType.STH]: 'sth',
            [DeliveryType.STM]: 'sth',
            [DeliveryType.AUTOSHIP]: 'sth',
        };

        const methodBase = table[normalizedMethod];

        return methodBase ? (`${methodBase} ${autoshipStatus}` as AutoshipSetupFulfillmentMethodOrder) : undefined;
    };

    const fulfillmentMethodOrder = normalizeFulfillmentMethodOrder(fulfillmentMethod);

    const eventPayload: Record<string, unknown> = {
        event: 'click_autoship_setup',
        value: item.salePrice,
        currency: cart?.currency,
        product_id: item.sku,
        product_price: Number((item.salePrice / item.quantity).toFixed(2)),
        product_quantity: item.quantity,
        ...(fulfillmentMethodOrder && { fulfillment_method_order: fulfillmentMethodOrder }),
    };

    dataLayerPush(eventPayload);
};

export const trackViewAutoshipInfoModal = (payload: { site_section: string }) => {
    dataLayerPush({
        event: 'view_autoship_info_modal',
        site_section: payload.site_section,
    });
};

const formatCountWithUnit = (count: number | null, singularUnit: string, pluralUnit: string): string | null => {
    if (count === null) return null;

    return `${count} ${count === 1 ? singularUnit : pluralUnit}`;
};

export const trackCalculateAutoshipFrequency = (payload: {
    totalPets: number | null;
    totalCupsFood: number | null;
}) => {
    dataLayerPush({
        event: 'calculate_autoship_frequency',
        total_pets: formatCountWithUnit(payload.totalPets, 'pet', 'pets'),
        total_cups_food: formatCountWithUnit(payload.totalCupsFood, 'cup', 'cups'),
    });
};

export const trackSetAutoshipFrequency = (payload: {
    totalPets: number | null;
    totalCupsFood: number | null;
    subscriptionFrequency: number | null;
    subscriptionFrequencyUnit: 'weeks' | 'months';
}) => {
    const singularFrequencyUnit = payload.subscriptionFrequencyUnit === 'weeks' ? 'week' : 'month';

    dataLayerPush({
        event: 'set_autoship_frequency',
        total_pets: formatCountWithUnit(payload.totalPets, 'pet', 'pets'),
        total_cups_food: formatCountWithUnit(payload.totalCupsFood, 'cup', 'cups'),
        subscription_frequency: formatCountWithUnit(
            payload.subscriptionFrequency,
            singularFrequencyUnit,
            payload.subscriptionFrequencyUnit
        ),
    });
};

export const trackFrequencyCalculatorClick = () => {
    dataLayerPush({
        event: 'click_autoship_frequency_calculator',
    });
};

export type AutoshipFrequencyCalculatorModalLinkText = 'reset' | 'close';

export const trackAutoshipFrequencyCalculatorModal = (payload: {
    link_text: AutoshipFrequencyCalculatorModalLinkText;
}) => {
    dataLayerPush({
        event: 'autoship_frequency_calculator_modal',
        link_text: payload.link_text,
    });
};

export const trackRemoveAutoship = (payload: { cart: Nullable<CartUI>; item: ItemUI; linkText?: string }) => {
    const { cart, item, linkText } = payload;
    dataLayerPush({
        event: 'remove_autoship_modal',
        link_text: linkText,
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        product_id: item.sku,
        product_price: Number((item.salePrice / item.quantity).toFixed(2)),
        product_quantity: item.quantity,
    });
};

export const trackChangeSubscriptionFrequency = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    previousFrequency: string;
    selectedFrequency: string;
}) => {
    const { cart, item, previousFrequency, selectedFrequency } = payload;
    dataLayerPush({
        event: 'change_subscription_frequency',
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        old_selection: previousFrequency,
        new_selection: selectedFrequency,
        product_id: item.sku,
        product_price: Number((item.salePrice / item.quantity).toFixed(2)),
        product_quantity: item.quantity,
    });
};

const parseItemData = (item: ItemUI) => ({
    value: item.salePrice,
    subscription_frequency: formatAutoshipFrequency(item.autoshipFrequency, item.autoshipFrequencyUnitOfMeasure),
    product_id: item.sku,
    product_price: Number((item.salePrice / item.quantity).toFixed(2)),
    product_quantity: item.quantity,
});

const buildGa4ItemTrackingFields = ({
    item,
    group,
    includeStockAvailability,
}: {
    item: ItemUI;
    group: FulfillmentGroupsItemUI;
    includeStockAvailability?: boolean;
}) => {
    const listAttribution = resolveGa4ItemListAttributionForCartItem(item);

    return {
        item_name: item.name,
        item_id: getGa4ItemId(item),
        quantity: item.quantity,
        price: Math.min(item.salePrice, item.basePrice),
        item_brand: item.merchandiseBrand,
        item_variant: item.sku,
        fulfillment_method: buildFulfillmentMethodLabel(group.fulfillmentMethod, item),
        autoship_frequency: formatAutoshipFrequency(item.autoshipFrequency, item.autoshipFrequencyUnitOfMeasure),
        store_id: group.storeId ?? null,
        size: item.variant?.size ?? null,
        flavor: item.variant?.flavor ?? null,
        color: item.variant?.color ?? null,
        item_cart_order: item.positionWithinCart,
        item_category: item.mhVicePresidentName,
        item_category2: item.mhDivision,
        item_category3: item.mhDepartment,
        item_category4: item.mhClass,
        item_category5: item.mhCategory,
        default_version: item.masterProductSku === item.sku ? 'yes' : 'no',

        item_list_name: listAttribution.item_list_name,
        item_list_id: listAttribution.item_list_id,
        ...(listAttribution.index !== undefined && { index: listAttribution.index }),
        item_search_term: resolveItemSearchTerm(
            getSkuSearchTerm(item.sku),
            listAttribution.item_list_id === NavigationListTypes.SEARCH ? (getSearchStats().searchTerm ?? null) : null
        ),
        ...(includeStockAvailability && {
            sth_availability: item?.sth_availability ?? '',
            bopis_availability: item?.bopis_availability ?? '',
            sdd_availability: item?.sdd_availability ?? '',
            sth_stock_availability: item?.sthStockAvailability ?? '',
            bopis_stock_availability: item?.bopisStockAvailability ?? '',
            sdd_stock_availability: item?.sddStockAvailability ?? '',
        }),
        scheduled_autoship_date: formatScheduledDeliveryDate(item.scheduledDeliveryDate),
        autoship_availability: item.autoshipEnabled ? 'yes' : 'no',
        autoship_autoselected: 'no',
    };
};

export const trackClickAutoshipScheduleChange = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    site_section: 'cart' | 'set up autoship drawer' | 'edit autoship drawer';
}) => {
    const { cart, item, site_section } = payload;

    dataLayerPush({
        event: 'click_schedule_autoship',
        currency: cart?.currency,
        ...parseItemData(item),
        site_section,
    });
};
export type AutoshipScheduleRemoveLinkText = 'keep start date' | 'remove start date' | 'close';
export const trackClickAutoshipScheduleRemove = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    link_text: AutoshipScheduleRemoveLinkText;
}) => {
    const { cart, item, link_text } = payload;

    dataLayerPush({
        event: 'remove_schedule_autoship_modal',
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        link_text,
        ...parseItemData(item),
    });
};

/** Sparky {@link StockStatusEnum} values and legacy labels → analytics `autoship_stock_availability` (lowercase, spaced). */
const formatAutoshipStockAvailabilityForDataLayer = (raw: string): string => {
    const fromSparky: Record<string, string> = {
        InStock: 'in stock',
        FewLeftInStock: 'low stock',
        OutOfStock: 'out of stock',
        NotAvailableInYourArea: 'not available',
    };
    const mapped = fromSparky[raw];
    if (mapped !== undefined) {
        return mapped;
    }

    return raw.replaceAll('_', ' ').replaceAll(/\s+/g, ' ').trim().toLowerCase();
};

const getFulfillmentMethodOrderForAddToSubscription = (
    cart: Nullable<CartUI>,
    item: ItemUI
): DeliveryTypeUnion | undefined => {
    const method = cart?.fulfillmentGroups
        ?.find(group => group.items.some(groupItem => groupItem.id === item.id || groupItem.sku === item.sku))
        ?.fulfillmentMethod?.toLowerCase();

    if (!method) {
        return undefined;
    }

    let methodBase: BaseDeliveryType;
    if (method === DeliveryType.STM || method === DeliveryType.AUTOSHIP) {
        methodBase = DeliveryType.STH;
    } else if (method === DeliveryType.BOPIS || method === DeliveryType.SDD || method === DeliveryType.STH) {
        methodBase = method;
    } else {
        return undefined;
    }

    if (!item.autoshipFrequency) {
        return methodBase;
    }
    return AUTOSHIP_DELIVERIES[methodBase];
};

export const trackAddToSubscription = (payload: {
    cart: Nullable<CartUI>;
    item: ItemUI;
    frequency: string;
    autoshipStockAvailability: string;
    /** Order Groove order public ID when adding to an existing upcoming Autoship order (e.g. cart flow). */
    subscriptionId?: string | null;
}) => {
    const { cart, item, frequency, autoshipStockAvailability, subscriptionId } = payload;
    const hasTargetOrderId = Boolean(subscriptionId);
    const fulfillmentFromCart = getFulfillmentMethodOrderForAddToSubscription(cart, item);
    const fulfillment_method_order: DeliveryTypeUnion = hasTargetOrderId
        ? AUTOSHIP_DELIVERIES.sth
        : (fulfillmentFromCart ?? AUTOSHIP_DELIVERIES.sth);

    dataLayerPush({
        event: 'add_to_subscription',
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: subscriptionId ?? item?.orderGrooveSubscriptionId ?? 'new',
        subscription_type: 'add to existing',
        subscription_frequency: frequency,
        fulfillment_method_order,
        product_id: item.sku,
        product_price: Number((item.salePrice / item.quantity).toFixed(2)),
        product_quantity: item.quantity,
        autoship_stock_availability: formatAutoshipStockAvailabilityForDataLayer(autoshipStockAvailability),
        autoship_autoselected: 'no',
        product_brand: item.merchandiseBrand,
        product_category: item.mhVicePresidentName,
        product_category2: item.mhDivision,
        product_category3: item.mhDepartment,
        product_category4: item.mhClass,
        product_category5: item.mhCategory,
    });
};

export const trackAddPaymentInfo = (
    cart: Nullable<CartUI>,
    paymentMethod: PaymentMethodsType,
    creditCardType?: CreditCardsType,
    isCrawl?: boolean
) => {
    dataLayerPush({ ecommerce: null });

    const items = cart?.fulfillmentGroups.flatMap(group =>
        group.items.map(item => buildGa4ItemTrackingFields({ item, includeStockAvailability: isCrawl, group }))
    );

    dataLayerPush({
        event: 'add_payment_info',
        ecommerce: {
            currency: cart?.currency,
            value: cart?.summary?.totalAmount ?? 0,
            coupon: getDiscountCodesString(cart?.discountCodes ?? []) ?? null,
            payment_type: creditCardType ?? paymentMethod,
            items,
        },
    });
};

export const trackAddDeliveryInstructionsClick = () => {
    dataLayerPush({
        event: 'click_add_delivery_instructions',
    });
};

export const trackSaveAutoshipSetup = (payload: { cart: Nullable<CartUI>; item: ItemUI; frequency: string }) => {
    const { cart, item, frequency } = payload;
    dataLayerPush({
        event: 'save_autoship_setup',
        value: item.salePrice,
        currency: cart?.currency,
        subscription_id: item?.orderGrooveSubscriptionId ?? 'new',
        subscription_frequency: frequency,
        product_id: item.sku,
        product_price: Number((item.salePrice / item.quantity).toFixed(2)),
        product_quantity: item.quantity,
    });
};

export const trackRemoveCart = (item: ItemUI) => {
    const products = [mapItem(item)];

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEremoveFromCart',
        ecommerce: {
            remove: {
                products,
            },
        },
    });
};

export const velocityTrackRemoveFromCart = (currency: Currency | undefined, items: TrackCartItem[]) => {
    const roughTotal = items.reduce<number>((acc, item) => acc + (item.salePrice ?? 0), 0);
    const updatedItems = items.map(({ salePrice, ...item }) => ({
        ...item,
        price: Number((item.price * 100).toFixed(2)),
    }));
    const roundedTotal = roundTotal(roughTotal);

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'remove_from_cart',
        ecommerce: {
            currency,
            value: roundedTotal,
            pdp_type: 'cart',
            category: '', // Skipped at this moment, in the future it would be integrated
            sub_category1: '', // Skipped at this moment, in the future it would be integrated
            sub_category2: '', // Skipped at this moment, in the future it would be integrated
            add_to_cart_type: 'decrease quantity',
            items: updatedItems,
        },
    });
};

export const trackClearCart = () => {
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'clearCart',
    });
};

export const trackBeginCheckout = (cart: CartUI, isCrawl?: boolean) => {
    dataLayerPush({ ecommerce: null });

    const items = mapCheckoutItems(cart, isCrawl);

    dataLayerPush({
        event: 'begin_checkout',
        ecommerce: {
            currency: cart.currency,
            value: cart.summary?.totalAmount,
            coupon: cart.discountCodes?.[0]?.code ?? null,
            items,
        },
    });
};

export const trackAddShippingInfo = (cart: CartUI, isCrawl?: boolean) => {
    dataLayerPush({ ecommerce: null });

    const items = mapCheckoutItems(cart, isCrawl);

    // Get shipping tier from the STH fulfillment group's shippingMethodName
    const sthGroup = cart.fulfillmentGroups.find(group => group.fulfillmentMethod?.toLowerCase() === 'sth');
    const shippingTier = sthGroup?.shippingMethodName || '';

    dataLayerPush({
        event: 'add_shipping_info',
        ecommerce: {
            currency: cart.currency,
            value: cart.summary?.totalAmount,
            shipping_tier: shippingTier,
            coupon: getDiscountCodesString(cart.discountCodes) ?? null,
            items,
        },
    });
};

const DELIVERY_INSTRUCTIONS_DROP_OFF_LOCATIONS = new Set([
    'front door',
    'back door',
    'side porch',
    'garage',
    'reception',
    'mailroom',
    'other',
    'no preference',
]);

export type DeliveryInstructionsTrackingFields = {
    deliveryLocation?: string | null;
    entryCode?: string | null;
    deliveryNotes?: string | null;
};

export type TrackDeliveryInstructionsInput = DeliveryInstructionsTrackingFields & {
    cart?: DeliveryInstructionsTrackingFields | null;
};

const hasDeliveryInstructionsValue = (value?: string | null) => Boolean(value?.trim());

const normalizeDropOffLocation = (deliveryLocation?: string | null): string | null => {
    const normalizedLocation = deliveryLocation?.trim().toLowerCase();

    if (!normalizedLocation || !DELIVERY_INSTRUCTIONS_DROP_OFF_LOCATIONS.has(normalizedLocation)) {
        return null;
    }

    return normalizedLocation;
};

const toYesNoProvidedFlag = (value?: string | null): YesNo => (hasDeliveryInstructionsValue(value) ? 'yes' : 'no');

const hasExistingDeliveryInstructions = (cart?: DeliveryInstructionsTrackingFields | null) =>
    hasDeliveryInstructionsValue(cart?.deliveryLocation) ||
    hasDeliveryInstructionsValue(cart?.entryCode) ||
    hasDeliveryInstructionsValue(cart?.deliveryNotes);

const hasDeliveryInstructionsFormValues = ({
    deliveryLocation,
    entryCode,
    deliveryNotes,
}: DeliveryInstructionsTrackingFields) =>
    hasDeliveryInstructionsValue(deliveryLocation) ||
    hasDeliveryInstructionsValue(entryCode) ||
    hasDeliveryInstructionsValue(deliveryNotes);

const buildDeliveryInstructionsPayload = (
    event: 'add_delivery_instructions' | 'edit_delivery_instructions',
    { deliveryLocation, entryCode, deliveryNotes }: DeliveryInstructionsTrackingFields
) => ({
    event,
    drop_off_location: normalizeDropOffLocation(deliveryLocation),
    gate_code_provided: toYesNoProvidedFlag(entryCode),
    delivery_notes_provided: toYesNoProvidedFlag(deliveryNotes),
});

export const buildAddDeliveryInstructionsPayload = (fields: DeliveryInstructionsTrackingFields) =>
    buildDeliveryInstructionsPayload('add_delivery_instructions', fields);

export const buildEditDeliveryInstructionsPayload = (fields: DeliveryInstructionsTrackingFields) =>
    buildDeliveryInstructionsPayload('edit_delivery_instructions', fields);

export const trackAddDeliveryInstructions = ({
    cart,
    deliveryLocation,
    entryCode,
    deliveryNotes,
}: TrackDeliveryInstructionsInput) => {
    if (
        hasExistingDeliveryInstructions(cart) ||
        !hasDeliveryInstructionsFormValues({ deliveryLocation, entryCode, deliveryNotes })
    ) {
        return;
    }

    dataLayerPush(buildAddDeliveryInstructionsPayload({ deliveryLocation, entryCode, deliveryNotes }));
};

export const trackEditDeliveryInstructions = ({
    cart,
    deliveryLocation,
    entryCode,
    deliveryNotes,
}: TrackDeliveryInstructionsInput) => {
    if (
        !hasExistingDeliveryInstructions(cart) ||
        !hasDeliveryInstructionsFormValues({ deliveryLocation, entryCode, deliveryNotes })
    ) {
        return;
    }

    dataLayerPush(buildEditDeliveryInstructionsPayload({ deliveryLocation, entryCode, deliveryNotes }));
};

export const trackAddPharmacyInfo = (cart: CartUI, isCrawl?: boolean) => {
    dataLayerPush({
        event: 'add_pharmacy_info',
        site_section: 'checkout',
    });
};

export const trackAddPet = (petType: string) => {
    dataLayerPush({
        event: 'add_pet',
        site_section: 'checkout | add pharmacy info',
        pet_type: petType.toLowerCase(),
    });
};

export const trackAddSddCheckoutInfo = (payload: { doorDashDeliveryTime: string; dasherTip: string }) => {
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'add_sdd_checkout_info',
        door_dash_delivery_time: payload.doorDashDeliveryTime,
        dasher_tip: payload.dasherTip,
    });
};

export const trackCheckout = (label: string, actionField: Record<string, unknown>) => {
    const labels = {
        complete: 'Review Order',
    };
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'EEcheckout',
        eventParameters: {
            category: 'Ecommerce',
            action: 'Checkout',
            label: labels[label as keyof typeof labels] ?? label,
        },
        ecommerce: {
            checkout: {
                actionField,
            },
        },
    });
};

export const trackCheckoutOption = (actionField: Record<string, unknown>) => {
    dataLayerPush({ ecommerce: null });

    dataLayerPush({
        event: 'EEcheckoutOption',
        ecommerce: {
            checkout: {
                actionField,
            },
        },
    });
};

export const trackPurchase = (
    purchaseData: Nullable<CartUI> | OrderUI,
    actionField: Record<string, unknown>,
    isOrderConfirmation?: boolean
) => {
    dataLayerPush({ ecommerce: null });

    const products = purchaseData?.fulfillmentGroups.flatMap(group =>
        group.items.map(item => {
            const listAttribution = resolveGa4ItemListAttributionForCartItem(item);

            const { category, subCategory } = extractCategoriesFromUrl(item.productUrl);

            return {
                name: item.name,
                id: item.sku,
                price: item.basePriceWithDiscounts ? item.basePriceWithDiscounts / 100 / item.quantity : 0,
                brand: item.merchandiseBrand,
                dimension9: category,
                dimension10: subCategory,
                dimension18: item.variant?.color ?? null,
                dimension19: item.variant?.size ?? null,
                dimension20: item.variant?.flavor ?? null,
                quantity: item.quantity,
                dimension5: item?.pharmacyType ?? 'Others',
                dimension21: item.customCaseQuantity,
                dimension13: item.autoshipFrequency,
                dimension14: group.storeId,
                dimension12: group.fulfillmentMethod?.toLowerCase(),
                dimension39: 'Yes',
                dimension41: item?.mhVicePresidentName ?? null,
                dimension42: item?.mhDivision ?? null,
                dimension43: item?.mhDepartment ?? null,
                dimension44: item?.mhClass ?? null,
                dimension45: item?.mhCategory ?? null,
                metric4: isOrderConfirmation ? item.basePrice / 100 : item.basePrice,
                metric5: isOrderConfirmation ? item.listPrice / 100 : item.listPrice,
                add_to_cart_type: item.pliAddedInStore ? 'in store' : 'add',
                dimension133: isOrderConfirmation ? null : 'PFM - Standard Add to Cart',
                item_list_name: listAttribution.item_list_name,
                item_list_id: listAttribution.item_list_id,
                ...(listAttribution.index !== undefined && { index: listAttribution.index }),
            };
        })
    );

    const eventTrackingProps = {
        event: 'EEpurchase',
        ecommerce: {
            currencyCode: purchaseData?.currency,
            purchase: {
                products,
                actionField,
            },
        },
    };

    logger.info('EEPurchase dataLayerPush argument: %o', eventTrackingProps);

    const gtmLoaded = window.dataLayer?.some(({ event }) => event == 'loaded-gtm');

    if (!gtmLoaded) logSentryError('GTM was not loaded');

    dataLayerPush(eventTrackingProps);
};

export const trackCheckoutSignUpImpression = () => {
    dataLayerPush({
        event: 'checkout-signup-impression',
    });
};

/** Data layer events for Appointments page */

type AppointmentEventProps = {
    service: string;
    breed?: string;
    selection?: string;
};

const trackAppointmentEvent = (event: string, { service, breed = '', selection }: AppointmentEventProps) => {
    if (event === 'click_upcoming_appt') {
        dataLayerPush({ event, selection, service: service.toLowerCase() });
    } else {
        dataLayerPush({ event, service: service.toLowerCase(), breed: breed?.toLowerCase() });
    }
};

export const trackUpcomingApptClick = (props: Pick<AppointmentEventProps, 'service' | 'selection'>) =>
    trackAppointmentEvent('click_upcoming_appt', props);

export const trackApptConfirmClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('confirm_appt', props);

export const trackRescheduleApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('reschedule_appt', props);

export const trackApptCancellationSuccess = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('cancel_appt', props);

export const trackKeepApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('keep_appt', props);

export const trackRebookApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('rebook_appt', props);

export const trackRedeemApptClick = (props: Pick<AppointmentEventProps, 'service' | 'breed'>) =>
    trackAppointmentEvent('redeem_appt', props);

export const trackClickToBookAppt = (linkUrl: string) => {
    dataLayerPush({
        event: 'click_to_book_appt',
        link_url: linkUrl,
        link_text: 'book appointment',
        site_section: 'my account | appointments',
    });
};

export const trackDeliveryNotAvailable = (zipCode: string) => {
    dataLayerPush({
        event: 'sched-deliv-not-available',
        'sched-deliv-not-available-zipcode': zipCode,
    });
};

export const trackBannerAlert = (siteSection: string, alertMessage: string) => {
    dataLayerPush({
        event: 'banner_alert',
        site_section: siteSection,
        alert_message: alertMessage.toLowerCase(),
    });
};

export type TrackSaveScheduleAutoshipPayload = {
    item: ItemUI;
    cart: Nullable<CartUI>;
    subscriptionFrequency: string;
};

export const trackSaveScheduleAutoship = (payload: TrackSaveScheduleAutoshipPayload) => {
    const { item, cart, subscriptionFrequency } = payload;

    dataLayerPush({
        event: 'save_schedule_autoship',
        value: item.salePrice.toFixed(2),
        currency: cart?.currency ?? null,
        subscription_frequency: subscriptionFrequency,
        product_id: item.sku,
        product_price: item.basePrice.toFixed(2),
        product_quantity: item.quantity,
    });
};

export const trackSelectMailIn = (sku: string) => {
    dataLayerPush({
        event: 'mail-in rx click',
        'mail-in rx click-SKU': sku,
    });
};

export type ProfileManagerCtaSelection =
    | 'update notifications'
    | 'manage autoship'
    | 'manage appointments'
    | 'back to pet profile';

export const trackProfileManagerClick = (selection: ProfileManagerCtaSelection) => {
    dataLayerPush({
        event: 'click_profile_manager',
        site_section: 'my account | about you | pet memorial | profile manager',
        selection,
    });
};

export const trackClickAddGotchaDay = () => {
    dataLayerPush({
        event: 'click_add_gotcha_day',
        site_section: 'my account | pets',
    });
};

export const trackMemorializePetSuccess = (petType: string) => {
    dataLayerPush({
        event: 'memorialize_pet',
        site_section: 'pet memorial',
        pet_type: petType,
    });
};

export const trackRestoreMemorializedPet = (petType: string) => {
    dataLayerPush({
        event: 'restore_memorialized_pet',
        site_section: 'my account | about you | pet memorial',
        pet_type: petType.toLowerCase(),
    });
};

export const trackProductClickInCart = ({
    item,
    list = 'cart',
    variant = 'traditional',
}: {
    item: ItemUI;
    list?: string;
    variant?: string;
}) => {
    const product = {
        id: item.sku,
        brand: item.merchandiseBrand,
        price: item.totalPrice,
        name: item.name,
        dimension5: 'Other',
        dimension6: list,
        dimension41: item.mhVicePresidentName,
        dimension42: item.mhDivision,
        dimension43: item.mhDepartment,
        dimension44: item.mhClass,
        dimension45: item.mhCategory,
        position: item.positionWithinCart,
    };

    dataLayerPush({ ecommerce: null });
    dataLayerPush({
        event: 'EEproductClick',
        ecommerce: {
            currencyCode,
            click: {
                actionField: {
                    list,
                },
                products: [product],
            },
        },
        variant,
    });
};

export const trackNavigationClick = (selection: string, linkUrl: string) => {
    dataLayerPush({
        event: 'nav_click',
        nav_section: 'my account nav',
        selection,
        link_url: linkUrl,
    });
};

export const trackNavigationHeaderClick = (selection: string, linkUrl: string, navSection: string = 'header nav') => {
    dataLayerPush({
        event: 'nav_click',
        nav_section: navSection,
        selection,
        link_url: linkUrl,
    });
};

export type TrackClickToShopParams = {
    linkText: string;
    linkUrl: string;
    siteSection: string;
};

export const trackClickToShop = ({ linkText, linkUrl, siteSection }: TrackClickToShopParams) => {
    dataLayerPush({
        event: 'click_to_shop',
        link_text: linkText,
        link_url: linkUrl,
        site_section: siteSection,
    });
};

export const trackChangeSubscriptionDate = (subscriptionDayOfMonth: number) => {
    dataLayerPush({
        event: 'change_subscription_date',
        subscription_day_of_month: subscriptionDayOfMonth,
    });
};

const formatFrequency = (frequency: number | null) => {
    if (!frequency) return null;
    const isWeek = frequency >= 7;
    const isMonth = frequency >= 90;

    let period: string = 'day';
    let frequencyValue: number = frequency;

    if (isMonth) {
        period = 'month';
        frequencyValue = frequency / 30;
    } else if (isWeek) {
        period = 'week';
        frequencyValue = frequency / 7;
    }

    return `every ${frequencyValue} ${period}${frequencyValue > 1 ? 's' : ''}`;
};

export const trackAutoship = (
    event: string,
    value: string | null,
    subscriptionId: string | null,
    currency: string | null,
    subscriptionFrequency: number | null,
    productId: string | null,
    productPrice: number | null,
    productQuantity: number | null,
    additionalData: Record<string, unknown> = {}
) => {
    dataLayerPush({
        event,
        value,
        currency,
        subscription_id: subscriptionId,
        product_id: productId,
        product_price: productPrice,
        ...(event !== 'change_subscription_frequency'
            ? {
                  subscription_frequency: formatFrequency(subscriptionFrequency),
              }
            : {
                  new_selection: formatFrequency(subscriptionFrequency),
              }),
        ...(event !== 'change_subscription_quantity' && { product_quantity: productQuantity }),
        ...additionalData,
    });
};

export const trackPlaceOrder = (order: OrderUI, isCrawl?: boolean, treatsEarned?: number | null) => {
    dataLayerPush({ ecommerce: null });

    const productItems = order.fulfillmentGroups.flatMap(group =>
        group.items.map(item => {
            const listAttribution = resolveGa4ItemListAttributionForCartItem(item);

            // Determine fulfillment method with scheduled autoship support
            // If scheduledDeliveryDate has a value, returns "sth autoship scheduled"
            const fulfillmentMethod = getFulfillmentMethodForItem(group.fulfillmentMethod, item);

            // Format scheduled delivery date to YYYYMMDD or null
            const scheduledAutoshipDate = formatScheduledDeliveryDate(item.scheduledDeliveryDate);

            return {
                item_name: item.name ?? null,
                item_id: getGa4ItemId(item),
                quantity: item.quantity ?? null,
                price:
                    item.salePrice < item.basePrice ? toDollarsAmount(item.salePrice) : toDollarsAmount(item.basePrice),
                fulfillment_method: fulfillmentMethod,
                scheduled_autoship_date: scheduledAutoshipDate,
                autoship_frequency:
                    item.autoshipFrequency && item.autoshipFrequencyUnitOfMeasure
                        ? formatAutoshipFrequency(item.autoshipFrequency, item.autoshipFrequencyUnitOfMeasure)
                        : null,
                store_id: group.storeId ?? null,
                size: item.variant?.size ?? null,
                flavor: item.variant?.flavor ?? null,
                color: item.variant?.color ?? null,
                item_cart_order: item.positionWithinCart ?? null,
                item_category: item.mhVicePresidentName ?? null,
                item_category2: item.mhDivision ?? null,
                item_category3: item.mhDepartment ?? null,
                item_category4: item.mhClass ?? null,
                item_category5: item.mhCategory ?? null,
                item_brand: item.merchandiseBrand ?? null,
                item_variant: item.sku ?? null,
                default_version: item.masterProductSku === item.sku ? 'yes' : 'no',
                autoship_availability: item.autoshipEnabled ? 'yes' : 'no',
                discount: toDollarsAmount(getItemDiscount(item)) ?? null,
                add_to_cart_type: item.pliAddedInStore ? 'in store' : 'add',
                item_list_name: listAttribution.item_list_name,
                item_list_id: listAttribution.item_list_id,
                ...(listAttribution.index !== undefined && { index: listAttribution.index }),
                item_search_term: resolveItemSearchTerm(
                    getSkuSearchTerm(item.sku),
                    listAttribution.item_list_id === NavigationListTypes.SEARCH
                        ? (getSearchStats().searchTerm ?? null)
                        : null
                ),
                coupon: null,
                ...(isCrawl
                    ? {
                          sth_availability: item?.sth_availability ?? '',
                          bopis_availability: item?.bopis_availability ?? '',
                          sdd_availability: item?.sdd_availability ?? '',
                          sth_stock_availability: item?.sthStockAvailability ?? '',
                          bopis_stock_availability: item?.bopisStockAvailability ?? '',
                          sdd_stock_availability: item?.sddStockAvailability ?? '',
                      }
                    : {
                          autoship_stock_availability: item.autoshipStockAvailability ?? null,
                      }),
            };
        })
    );

    const donationAmount = order.summary?.donationAmount;
    const items = [
        ...productItems,
        ...(donationAmount && donationAmount > 0
            ? [
                  {
                      add_to_cart_type: 'add',
                      item_cart_order: productItems.length + 1,
                      item_category: 'donation',
                      item_id: '00000000',
                      item_name: 'donation',
                      price: toDollarsAmount(donationAmount).toFixed(2),
                      quantity: 1,
                  },
              ]
            : []),
    ];

    // Get shipping tier from the STH fulfillment group's shippingMethodName
    const sthGroup = order.fulfillmentGroups.find(group => group.fulfillmentMethod?.toLowerCase() === 'sth');
    const shippingTier = sthGroup?.shippingMethodName ?? null;

    dataLayerPush({
        event: 'purchase',
        ecommerce: {
            transaction_id: order.orderNumber ?? null,
            currency: order.currency ?? null,
            value: toDollarsAmount(order.summary?.totalAmount) ?? null,
            coupon: getDiscountCodesString(order.discountCodes) || null, // Keep `||` here because we want `null` if there areno discount codes
            shipping: toDollarsAmount(order.summary?.shippingCost) ?? null,
            tax: toDollarsAmount(order.summary?.taxAmounts.total) ?? null,
            payment_type: getPaymentMethodsString(order.paymentMethods) || null,
            shipping_tier: shippingTier,
            discount_order: toDollarsAmount(order.summary.savingsAmount) ?? null,
            treats_earned: treatsEarned ?? null,
            treats_used: order.treatPointsApplied ?? null,
            fulfillment_method_order: getFulfillmentMethodsString(order.fulfillmentGroups) || null, // Keep `||` here because we want `null` if there areno fulfillment methods
            items,
        },
    });
};
